/**
 * Built-in Unlayer email template designs.
 * Each `design` is a valid Unlayer JSONTemplate that can be passed to
 * editor.loadDesign(design).
 *
 * Thumbnails are lightweight inline SVGs that visually represent the layout.
 */

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  thumbnail: string; // SVG string
  design: Record<string, unknown>;
}

// ─── Thumbnail helpers ────────────────────────────────────────────────────────

const svg = (content: string) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 260" width="200" height="260">${content}</svg>`,
  )}`;

const bg = '<rect width="200" height="260" fill="#f8f8f8" rx="4"/>';
const card = (y: number, h: number, fill = '#e4e4e7') =>
  `<rect x="16" y="${y}" width="168" height="${h}" fill="${fill}" rx="3"/>`;
const line = (y: number, w: number, fill = '#d4d4d8') =>
  `<rect x="16" y="${y}" width="${w}" height="8" fill="${fill}" rx="2"/>`;
const btn = (y: number, fill = '#6366f1') =>
  `<rect x="56" y="${y}" width="88" height="22" fill="${fill}" rx="4"/>`;
const img = (y: number, h = 60) => card(y, h, '#c4b5fd');
const cols2 = (y: number, h: number) =>
  `<rect x="16" y="${y}" width="78" height="${h}" fill="#e4e4e7" rx="3"/><rect x="106" y="${y}" width="78" height="${h}" fill="#e4e4e7" rx="3"/>`;

// ─── Templates ────────────────────────────────────────────────────────────────

export const EMAIL_TEMPLATES: EmailTemplate[] = [
  // ── 1. Welcome ──────────────────────────────────────────────────────────────
  {
    id: 'welcome',
    name: 'Welcome',
    description: 'Onboard new users with a warm hello',
    thumbnail: svg(
      bg +
        card(16, 32, '#6366f1') +
        img(60) +
        line(134, 120) +
        line(148, 90, '#e4e4e7') +
        line(162, 100, '#e4e4e7') +
        btn(180) +
        line(218, 80, '#d4d4d8') +
        line(230, 60, '#e4e4e7'),
    ),
    design: {
      counters: { u_row: 4, u_column: 4, u_content_text: 3, u_content_button: 1, u_content_divider: 1, u_content_image: 1 },
      body: {
        id: 'tpl-welcome',
        rows: [
          // Header band
          {
            id: 'r1', cells: [1],
            columns: [{
              id: 'c1', contents: [{
                id: 'i1', type: 'image',
                values: {
                  src: { url: 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="80"><rect width="600" height="80" fill="#6366f1"/><text x="300" y="47" font-family="Arial,sans-serif" font-size="22" font-weight="700" fill="#ffffff" text-anchor="middle" letter-spacing="2">YourBrand</text></svg>'), width: 600, height: 80 },
                  textAlign: 'center', containerPadding: '16px', _meta: { htmlID: 'u_content_image_1' },
                },
              }],
              values: { backgroundColor: '#6366f1', padding: '0px', _meta: { htmlID: 'u_column_1' } },
            }],
            values: { backgroundColor: '#6366f1', columnsBackgroundColor: '', _meta: { htmlID: 'u_row_1' } },
          },
          // Hero text
          {
            id: 'r2', cells: [1],
            columns: [{
              id: 'c2', contents: [{
                id: 'i2', type: 'text',
                values: {
                   text: '<h1 style="text-align:center;font-family:Arial,sans-serif;color:#18181b;margin:0 0 12px">Welcome, {{customer.firstName}}! 👋</h1><p style="text-align:center;font-family:Arial,sans-serif;color:#52525b;font-size:16px;line-height:1.6">We\'re thrilled to have you on board. Your account is ready — let\'s get started!</p>',
                  containerPadding: '40px 32px 24px',
                  _meta: { htmlID: 'u_content_text_1' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_2' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_2' } },
          },
          // CTA button
          {
            id: 'r3', cells: [1],
            columns: [{
              id: 'c3', contents: [{
                id: 'i3', type: 'button',
                values: {
                  text: 'Get Started',
                   href: { name: 'web', values: { href: '{{event.ctaUrl}}', target: '_blank' } },
                  textAlign: 'center', containerPadding: '8px 32px 40px',
                  buttonColors: { color: '#ffffff', backgroundColor: '#6366f1', hoverColor: '#ffffff', hoverBackgroundColor: '#4f46e5' },
                  padding: '14px 32px', borderRadius: '6px',
                  _meta: { htmlID: 'u_content_button_1' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_3' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_3' } },
          },
          // Footer
          {
            id: 'r4', cells: [1],
            columns: [{
              id: 'c4', contents: [{
                id: 'i4', type: 'text',
                values: {
                   text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#a1a1aa;font-size:12px;line-height:1.5">You received this email because you signed up.<br>© 2026 YourBrand · <a href="{{system.unsubscribeUrl}}" style="color:#a1a1aa">Unsubscribe</a></p>',
                  containerPadding: '20px 32px',
                  _meta: { htmlID: 'u_content_text_3' },
                },
              }],
              values: { backgroundColor: '#f4f4f5', padding: '0px', _meta: { htmlID: 'u_column_4' } },
            }],
            values: { backgroundColor: '#f4f4f5', _meta: { htmlID: 'u_row_4' } },
          },
        ],
        values: {
          backgroundColor: '#f4f4f5', contentWidth: '600px', fontFamily: { label: 'Arial', value: 'arial,helvetica,sans-serif' },
          _meta: { htmlID: 'u_body' },
        },
      },
    },
  },

  // ── 2. Newsletter ────────────────────────────────────────────────────────────
  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'Two-column layout for digests and updates',
    thumbnail: svg(
      bg +
        card(16, 24, '#6366f1') +
        line(52, 140) +
        cols2(68, 80) +
        line(162, 100, '#e4e4e7') +
        line(174, 80, '#e4e4e7') +
        line(190, 60, '#d4d4d8') +
        line(218, 80, '#d4d4d8') +
        line(230, 60, '#e4e4e7'),
    ),
    design: {
      counters: { u_row: 4, u_column: 5, u_content_text: 5, u_content_divider: 1 },
      body: {
        id: 'tpl-newsletter',
        rows: [
          // Header
          {
            id: 'r1', cells: [1],
            columns: [{
              id: 'c1', contents: [{
                id: 'i1', type: 'text',
                values: {
                  text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#ffffff;font-size:22px;font-weight:bold;margin:0">📰 Monthly Newsletter</p>',
                  containerPadding: '20px 24px',
                  _meta: { htmlID: 'u_content_text_1' },
                },
              }],
              values: { backgroundColor: '#6366f1', padding: '0px', _meta: { htmlID: 'u_column_1' } },
            }],
            values: { backgroundColor: '#6366f1', _meta: { htmlID: 'u_row_1' } },
          },
          // Intro
          {
            id: 'r2', cells: [1],
            columns: [{
              id: 'c2', contents: [{
                id: 'i2', type: 'text',
                values: {
                   text: '<p style="font-family:Arial,sans-serif;color:#3f3f46;font-size:15px;line-height:1.6">Hi {{customer.firstName}},<br><br>Here\'s what\'s happening this month. We\'ve rounded up the best stories, updates, and tips just for you.</p>',
                  containerPadding: '28px 32px 16px',
                  _meta: { htmlID: 'u_content_text_2' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_2' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_2' } },
          },
          // Two columns
          {
            id: 'r3', cells: [1, 1],
            columns: [
              {
                id: 'c3', contents: [{
                  id: 'i3', type: 'text',
                  values: {
                    text: '<h3 style="font-family:Arial,sans-serif;color:#18181b;margin:0 0 8px">Story One</h3><p style="font-family:Arial,sans-serif;color:#71717a;font-size:14px;line-height:1.5">A short teaser for your first article or update goes here. Keep it punchy.</p><a href="#" style="font-family:Arial,sans-serif;color:#6366f1;font-size:14px">Read more →</a>',
                    containerPadding: '16px',
                    _meta: { htmlID: 'u_content_text_3' },
                  },
                }],
                values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_3' } },
              },
              {
                id: 'c4', contents: [{
                  id: 'i4', type: 'text',
                  values: {
                    text: '<h3 style="font-family:Arial,sans-serif;color:#18181b;margin:0 0 8px">Story Two</h3><p style="font-family:Arial,sans-serif;color:#71717a;font-size:14px;line-height:1.5">A short teaser for your second article or update goes here. Keep it punchy.</p><a href="#" style="font-family:Arial,sans-serif;color:#6366f1;font-size:14px">Read more →</a>',
                    containerPadding: '16px',
                    _meta: { htmlID: 'u_content_text_4' },
                  },
                }],
                values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_4' } },
              },
            ],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_3' } },
          },
          // Footer
          {
            id: 'r4', cells: [1],
            columns: [{
              id: 'c5', contents: [{
                id: 'i5', type: 'text',
                values: {
                   text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#a1a1aa;font-size:12px">© 2026 YourBrand · <a href="{{system.unsubscribeUrl}}" style="color:#a1a1aa">Unsubscribe</a></p>',
                  containerPadding: '20px 32px',
                  _meta: { htmlID: 'u_content_text_5' },
                },
              }],
              values: { backgroundColor: '#f4f4f5', padding: '0px', _meta: { htmlID: 'u_column_5' } },
            }],
            values: { backgroundColor: '#f4f4f5', _meta: { htmlID: 'u_row_4' } },
          },
        ],
        values: {
          backgroundColor: '#f4f4f5', contentWidth: '600px',
          fontFamily: { label: 'Arial', value: 'arial,helvetica,sans-serif' },
          _meta: { htmlID: 'u_body' },
        },
      },
    },
  },

  // ── 3. Promotional ──────────────────────────────────────────────────────────
  {
    id: 'promotional',
    name: 'Promotional',
    description: 'Flash sale, discount code or special offer',
    thumbnail: svg(
      bg +
        img(16, 72) +
        line(100, 110, '#18181b') +
        line(114, 80, '#6366f1') +
        line(130, 130, '#e4e4e7') +
        line(144, 100, '#e4e4e7') +
        btn(162, '#ef4444') +
        line(200, 80, '#d4d4d8') +
        line(214, 60, '#e4e4e7'),
    ),
    design: {
      counters: { u_row: 4, u_column: 4, u_content_text: 4, u_content_button: 1, u_content_image: 1 },
      body: {
        id: 'tpl-promo',
        rows: [
          // Banner image
          {
            id: 'r1', cells: [1],
            columns: [{
              id: 'c1', contents: [{
                id: 'i1', type: 'image',
                values: {
                  src: { url: 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="200"><rect width="600" height="200" fill="#ef4444"/><text x="300" y="110" font-family="Arial,sans-serif" font-size="56" font-weight="900" fill="#ffffff" text-anchor="middle">SALE</text></svg>'), width: 600, height: 200 },
                  textAlign: 'center', containerPadding: '0px',
                  _meta: { htmlID: 'u_content_image_1' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_1' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_1' } },
          },
          // Headline + offer
          {
            id: 'r2', cells: [1],
            columns: [{
              id: 'c2', contents: [{
                id: 'i2', type: 'text',
                values: {
                   text: '<h1 style="text-align:center;font-family:Arial,sans-serif;color:#18181b;font-size:32px;margin:0 0 8px">🔥 50% OFF Everything</h1><p style="text-align:center;font-family:Arial,sans-serif;color:#6366f1;font-size:18px;font-weight:bold;margin:0 0 16px">Use code: <span style="background:#f0f0ff;padding:4px 10px;border-radius:4px;letter-spacing:2px">{{event.couponCode}}</span></p><p style="text-align:center;font-family:Arial,sans-serif;color:#71717a;font-size:15px;line-height:1.5">Offer valid until {{event.expiryDate}}. Don\'t miss out — limited time only!</p>',
                  containerPadding: '36px 32px 24px',
                  _meta: { htmlID: 'u_content_text_2' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_2' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_2' } },
          },
          // CTA
          {
            id: 'r3', cells: [1],
            columns: [{
              id: 'c3', contents: [{
                id: 'i3', type: 'button',
                values: {
                  text: 'Shop Now →',
                   href: { name: 'web', values: { href: '{{event.shopUrl}}', target: '_blank' } },
                  textAlign: 'center', containerPadding: '8px 32px 40px',
                  buttonColors: { color: '#ffffff', backgroundColor: '#ef4444', hoverColor: '#ffffff', hoverBackgroundColor: '#dc2626' },
                  padding: '16px 48px', borderRadius: '6px',
                  fontSize: { value: '16px' }, fontWeight: 700,
                  _meta: { htmlID: 'u_content_button_1' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_3' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_3' } },
          },
          // Footer
          {
            id: 'r4', cells: [1],
            columns: [{
              id: 'c4', contents: [{
                id: 'i4', type: 'text',
                values: {
                  text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#a1a1aa;font-size:12px">© 2026 YourBrand · <a href="{{system.unsubscribeUrl}}" style="color:#a1a1aa">Unsubscribe</a></p>',
                  containerPadding: '20px 32px',
                  _meta: { htmlID: 'u_content_text_4' },
                },
              }],
              values: { backgroundColor: '#f4f4f5', padding: '0px', _meta: { htmlID: 'u_column_4' } },
            }],
            values: { backgroundColor: '#f4f4f5', _meta: { htmlID: 'u_row_4' } },
          },
        ],
        values: {
          backgroundColor: '#f4f4f5', contentWidth: '600px',
          fontFamily: { label: 'Arial', value: 'arial,helvetica,sans-serif' },
          _meta: { htmlID: 'u_body' },
        },
      },
    },
  },

  // ── 4. Transactional ────────────────────────────────────────────────────────
  {
    id: 'transactional',
    name: 'Transactional',
    description: 'Order confirmation or receipt',
    thumbnail: svg(
      bg +
        card(16, 28, '#18181b') +
        line(56, 120) +
        line(70, 90, '#e4e4e7') +
        card(90, 60, '#f4f4f5') +
        line(96, 80, '#d4d4d8') +
        line(108, 60, '#d4d4d8') +
        line(120, 80, '#d4d4d8') +
        line(132, 60, '#d4d4d8') +
        line(162, 100) +
        line(176, 80, '#e4e4e7') +
        btn(196, '#18181b') +
        line(230, 80, '#d4d4d8'),
    ),
    design: {
      counters: { u_row: 4, u_column: 4, u_content_text: 5, u_content_button: 1 },
      body: {
        id: 'tpl-transactional',
        rows: [
          // Header
          {
            id: 'r1', cells: [1],
            columns: [{
              id: 'c1', contents: [{
                id: 'i1', type: 'text',
                values: {
                  text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#ffffff;font-size:18px;font-weight:bold;margin:0">✅ Order Confirmed</p>',
                  containerPadding: '20px 24px',
                  _meta: { htmlID: 'u_content_text_1' },
                },
              }],
              values: { backgroundColor: '#18181b', padding: '0px', _meta: { htmlID: 'u_column_1' } },
            }],
            values: { backgroundColor: '#18181b', _meta: { htmlID: 'u_row_1' } },
          },
          // Greeting
          {
            id: 'r2', cells: [1],
            columns: [{
              id: 'c2', contents: [{
                id: 'i2', type: 'text',
                values: {
                   text: '<p style="font-family:Arial,sans-serif;color:#3f3f46;font-size:15px;line-height:1.6">Hi {{customer.firstName}},<br><br>Thanks for your order! Here\'s your summary:</p>',
                  containerPadding: '28px 32px 16px',
                  _meta: { htmlID: 'u_content_text_2' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_2' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_2' } },
          },
          // Order summary table
          {
            id: 'r3', cells: [1],
            columns: [{
              id: 'c3', contents: [{
                id: 'i3', type: 'text',
                values: {
                  text: `<table width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;border-collapse:collapse">
  <tr style="border-bottom:1px solid #e4e4e7">
    <td style="padding:8px 0;color:#71717a;font-size:13px">Order #</td>
     <td style="padding:8px 0;color:#18181b;font-size:13px;text-align:right;font-weight:bold">{{event.orderId}}</td>
   </tr>
   <tr style="border-bottom:1px solid #e4e4e7">
     <td style="padding:8px 0;color:#71717a;font-size:13px">Date</td>
     <td style="padding:8px 0;color:#18181b;font-size:13px;text-align:right">{{event.orderDate}}</td>
   </tr>
   <tr style="border-bottom:1px solid #e4e4e7">
     <td style="padding:8px 0;color:#71717a;font-size:13px">Items</td>
     <td style="padding:8px 0;color:#18181b;font-size:13px;text-align:right">{{event.itemCount}}</td>
   </tr>
   <tr>
     <td style="padding:12px 0;color:#18181b;font-size:15px;font-weight:bold">Total</td>
     <td style="padding:12px 0;color:#18181b;font-size:15px;font-weight:bold;text-align:right">{{event.orderTotal}}</td>
  </tr>
</table>`,
                  containerPadding: '8px 32px 24px',
                  _meta: { htmlID: 'u_content_text_3' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_3' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_3' } },
          },
          // CTA + footer
          {
            id: 'r4', cells: [1],
            columns: [{
              id: 'c4', contents: [
                {
                  id: 'i4', type: 'button',
                  values: {
                    text: 'Track My Order',
                     href: { name: 'web', values: { href: '{{event.trackingUrl}}', target: '_blank' } },
                    textAlign: 'center', containerPadding: '24px 32px 16px',
                    buttonColors: { color: '#ffffff', backgroundColor: '#18181b', hoverColor: '#ffffff', hoverBackgroundColor: '#3f3f46' },
                    padding: '14px 32px', borderRadius: '6px',
                    _meta: { htmlID: 'u_content_button_1' },
                  },
                },
                {
                  id: 'i5', type: 'text',
                  values: {
                    text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#a1a1aa;font-size:12px">© 2026 YourBrand · <a href="{{system.unsubscribeUrl}}" style="color:#a1a1aa">Unsubscribe</a></p>',
                    containerPadding: '16px 32px 20px',
                    _meta: { htmlID: 'u_content_text_5' },
                  },
                },
              ],
              values: { backgroundColor: '#f4f4f5', padding: '0px', _meta: { htmlID: 'u_column_4' } },
            }],
            values: { backgroundColor: '#f4f4f5', _meta: { htmlID: 'u_row_4' } },
          },
        ],
        values: {
          backgroundColor: '#f4f4f5', contentWidth: '600px',
          fontFamily: { label: 'Arial', value: 'arial,helvetica,sans-serif' },
          _meta: { htmlID: 'u_body' },
        },
      },
    },
  },

  // ── 5. Announcement ─────────────────────────────────────────────────────────
  {
    id: 'announcement',
    name: 'Announcement',
    description: 'Product launch or company news',
    thumbnail: svg(
      bg +
        card(16, 20, '#f0f9ff') +
        line(26, 100, '#0ea5e9') +
        img(48, 56) +
        line(116, 130, '#18181b') +
        line(130, 100, '#18181b') +
        line(148, 130, '#e4e4e7') +
        line(162, 100, '#e4e4e7') +
        line(176, 80, '#e4e4e7') +
        btn(196, '#0ea5e9') +
        line(230, 80, '#d4d4d8'),
    ),
    design: {
      counters: { u_row: 4, u_column: 4, u_content_text: 4, u_content_button: 1, u_content_image: 1 },
      body: {
        id: 'tpl-announcement',
        rows: [
          // Top bar
          {
            id: 'r1', cells: [1],
            columns: [{
              id: 'c1', contents: [{
                id: 'i1', type: 'text',
                values: {
                  text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#0369a1;font-size:13px;margin:0;letter-spacing:1px;text-transform:uppercase;font-weight:600">🚀 Big News</p>',
                  containerPadding: '14px 24px',
                  _meta: { htmlID: 'u_content_text_1' },
                },
              }],
              values: { backgroundColor: '#f0f9ff', padding: '0px', _meta: { htmlID: 'u_column_1' } },
            }],
            values: { backgroundColor: '#f0f9ff', _meta: { htmlID: 'u_row_1' } },
          },
          // Hero image
          {
            id: 'r2', cells: [1],
            columns: [{
              id: 'c2', contents: [{
                id: 'i2', type: 'image',
                values: {
                  src: { url: 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="220"><rect width="600" height="220" fill="#0ea5e9"/><text x="300" y="120" font-family="Arial,sans-serif" font-size="28" font-weight="700" fill="#ffffff" text-anchor="middle">New Feature</text></svg>'), width: 600, height: 220 },
                  textAlign: 'center', containerPadding: '0px',
                  _meta: { htmlID: 'u_content_image_1' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_2' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_2' } },
          },
          // Content
          {
            id: 'r3', cells: [1],
            columns: [{
              id: 'c3', contents: [
                {
                  id: 'i3', type: 'text',
                  values: {
                     text: '<h1 style="text-align:center;font-family:Arial,sans-serif;color:#18181b;font-size:26px;margin:0 0 16px">Introducing {{event.featureName}}</h1><p style="font-family:Arial,sans-serif;color:#52525b;font-size:15px;line-height:1.7;margin:0 0 12px">We\'ve been working hard on something special — and today it\'s finally here. {{event.featureName}} is designed to help you {{event.benefitDescription}}.</p><p style="font-family:Arial,sans-serif;color:#52525b;font-size:15px;line-height:1.7">Available now for all {{event.planName}} users. Upgrade or log in to start using it today.</p>',
                    containerPadding: '36px 32px 24px',
                    _meta: { htmlID: 'u_content_text_3' },
                  },
                },
                {
                  id: 'i4', type: 'button',
                  values: {
                    text: 'See What\'s New →',
                     href: { name: 'web', values: { href: '{{event.announcementUrl}}', target: '_blank' } },
                    textAlign: 'center', containerPadding: '0 32px 36px',
                    buttonColors: { color: '#ffffff', backgroundColor: '#0ea5e9', hoverColor: '#ffffff', hoverBackgroundColor: '#0284c7' },
                    padding: '14px 32px', borderRadius: '6px',
                    _meta: { htmlID: 'u_content_button_1' },
                  },
                },
              ],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_3' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_3' } },
          },
          // Footer
          {
            id: 'r4', cells: [1],
            columns: [{
              id: 'c4', contents: [{
                id: 'i5', type: 'text',
                values: {
                  text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#a1a1aa;font-size:12px">© 2026 YourBrand · <a href="{{system.unsubscribeUrl}}" style="color:#a1a1aa">Unsubscribe</a></p>',
                  containerPadding: '20px 32px',
                  _meta: { htmlID: 'u_content_text_4' },
                },
              }],
              values: { backgroundColor: '#f4f4f5', padding: '0px', _meta: { htmlID: 'u_column_4' } },
            }],
            values: { backgroundColor: '#f4f4f5', _meta: { htmlID: 'u_row_4' } },
          },
        ],
        values: {
          backgroundColor: '#f4f4f5', contentWidth: '600px',
          fontFamily: { label: 'Arial', value: 'arial,helvetica,sans-serif' },
          _meta: { htmlID: 'u_body' },
        },
      },
    },
  },

  // ── 6. Re-engagement ────────────────────────────────────────────────────────
  {
    id: 're-engagement',
    name: 'Re-engagement',
    description: 'Win back inactive users',
    thumbnail: svg(
      bg +
        card(16, 100, '#fef3c7') +
        line(36, 90, '#92400e') +
        line(50, 120, '#92400e') +
        line(78, 100, '#d97706') +
        btn(108, '#f59e0b') +
        line(140, 130, '#e4e4e7') +
        line(154, 100, '#e4e4e7') +
        line(170, 80, '#e4e4e7') +
        line(200, 80, '#d4d4d8') +
        line(214, 60, '#e4e4e7'),
    ),
    design: {
      counters: { u_row: 3, u_column: 3, u_content_text: 3, u_content_button: 1 },
      body: {
        id: 'tpl-reengagement',
        rows: [
          // Hero CTA block
          {
            id: 'r1', cells: [1],
            columns: [{
              id: 'c1', contents: [
                {
                  id: 'i1', type: 'text',
                  values: {
                     text: '<p style="text-align:center;font-family:Arial,sans-serif;font-size:48px;margin:0 0 8px">😢</p><h1 style="text-align:center;font-family:Arial,sans-serif;color:#78350f;font-size:26px;margin:0 0 12px">We miss you, {{customer.firstName}}!</h1><p style="text-align:center;font-family:Arial,sans-serif;color:#92400e;font-size:15px;line-height:1.6;margin:0 0 8px">It\'s been a while since we last saw you. A lot has changed — come see what\'s new!</p>',
                    containerPadding: '40px 32px 12px',
                    _meta: { htmlID: 'u_content_text_1' },
                  },
                },
                {
                  id: 'i2', type: 'button',
                  values: {
                    text: 'Come Back & Explore',
                     href: { name: 'web', values: { href: '{{event.loginUrl}}', target: '_blank' } },
                    textAlign: 'center', containerPadding: '8px 32px 40px',
                    buttonColors: { color: '#ffffff', backgroundColor: '#f59e0b', hoverColor: '#ffffff', hoverBackgroundColor: '#d97706' },
                    padding: '16px 40px', borderRadius: '6px',
                    fontSize: { value: '15px' }, fontWeight: 700,
                    _meta: { htmlID: 'u_content_button_1' },
                  },
                },
              ],
              values: { backgroundColor: '#fef3c7', padding: '0px', _meta: { htmlID: 'u_column_1' } },
            }],
            values: { backgroundColor: '#fef3c7', _meta: { htmlID: 'u_row_1' } },
          },
          // What's new
          {
            id: 'r2', cells: [1],
            columns: [{
              id: 'c2', contents: [{
                id: 'i3', type: 'text',
                values: {
                   text: '<h2 style="font-family:Arial,sans-serif;color:#18181b;font-size:18px;margin:0 0 12px">Here\'s what you\'ve missed:</h2><ul style="font-family:Arial,sans-serif;color:#52525b;font-size:15px;line-height:1.8;margin:0;padding-left:20px"><li>{{event.update1}}</li><li>{{event.update2}}</li><li>{{event.update3}}</li></ul>',
                  containerPadding: '32px 32px 28px',
                  _meta: { htmlID: 'u_content_text_2' },
                },
              }],
              values: { backgroundColor: '#ffffff', padding: '0px', _meta: { htmlID: 'u_column_2' } },
            }],
            values: { backgroundColor: '#ffffff', _meta: { htmlID: 'u_row_2' } },
          },
          // Footer
          {
            id: 'r3', cells: [1],
            columns: [{
              id: 'c3', contents: [{
                id: 'i4', type: 'text',
                values: {
                  text: '<p style="text-align:center;font-family:Arial,sans-serif;color:#a1a1aa;font-size:12px">© 2026 YourBrand · <a href="{{system.unsubscribeUrl}}" style="color:#a1a1aa">Unsubscribe</a></p>',
                  containerPadding: '20px 32px',
                  _meta: { htmlID: 'u_content_text_3' },
                },
              }],
              values: { backgroundColor: '#f4f4f5', padding: '0px', _meta: { htmlID: 'u_column_3' } },
            }],
            values: { backgroundColor: '#f4f4f5', _meta: { htmlID: 'u_row_3' } },
          },
        ],
        values: {
          backgroundColor: '#f4f4f5', contentWidth: '600px',
          fontFamily: { label: 'Arial', value: 'arial,helvetica,sans-serif' },
          _meta: { htmlID: 'u_body' },
        },
      },
    },
  },
];
